import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { fr } from "date-fns/locale";
import { Calendar } from "../components/ui/calendar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import PassesFlow from "../components/PassesFlow";
import api from "../lib/api";
import { eur, spaceTypeLabel, slotLabel, formatApiError } from "../lib/utils.helpers";
import { useAuth } from "../contexts/AuthContext";

export default function Reserver() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reservationMode, setReservationMode] = useState("carte"); // "carte" | "pass"
  const [spaces, setSpaces] = useState([]);
  const [spaceId, setSpaceId] = useState(params.get("space") || "");
  const [date, setDate] = useState(new Date());
  const [bookingMode, setBookingMode] = useState("single"); // single | range
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [availability, setAvailability] = useState(null);
  const [unavailable, setUnavailable] = useState([]); // ISO fully booked
  const [amOnly, setAmOnly] = useState([]);
  const [pmOnly, setPmOnly] = useState([]);
  const [slotType, setSlotType] = useState("full_day");
  const [hourStart, setHourStart] = useState(null);
  const [seats, setSeats] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [submitting, setSubmitting] = useState(false);
  const [myPasses, setMyPasses] = useState([]);

  const space = useMemo(() => spaces.find((s) => s.id === spaceId), [spaces, spaceId]);
  const isoDate = useMemo(() => {
    const d = date instanceof Date ? date : new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  }, [date]);

  useEffect(() => {
    if (!user) { setMyPasses([]); return; }
    api.get("/passes/my").then((r) => setMyPasses(r.data || [])).catch(() => setMyPasses([]));
  }, [user]);

  const activePass = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return myPasses.find((p) => p.status === "active" && (!p.valid_until || p.valid_until >= today));
  }, [myPasses]);

  const passUsable = useMemo(() => {
    if (!space || !activePass || space.type !== "open_space") return false;
    if (bookingMode === "single" && slotType !== "full_day") return false;
    if (bookingMode === "range") {
      if (!dateRange.from || !dateRange.to) return true;
      const days = Math.round((dateRange.to - dateRange.from) / 86400000) + 1;
      if (activePass.unlimited) return true;
      return (activePass.days_remaining || 0) >= days;
    }
    if (activePass.unlimited) return true;
    return (activePass.days_remaining || 0) >= 1;
  }, [space, activePass, bookingMode, slotType, dateRange]);

  useEffect(() => {
    api.get("/spaces").then((r) => {
      setSpaces(r.data);
      if (!spaceId && r.data[0]) setSpaceId(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!spaceId) return;
    api.get(`/spaces/${spaceId}/availability`, { params: { date: isoDate } })
      .then((r) => setAvailability(r.data))
      .catch(() => setAvailability(null));
  }, [spaceId, isoDate]);

  // Fetch fully-booked / blocked dates for the next 3 months to grey out on calendar
  useEffect(() => {
    if (!spaceId) return;
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 3, 0);
    const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    api.get(`/spaces/${spaceId}/unavailable-dates`, { params: { start: iso(start), end: iso(end) } })
      .then((r) => {
        setUnavailable(r.data.unavailable || []);
        setAmOnly(r.data.am_only || []);
        setPmOnly(r.data.pm_only || []);
      })
      .catch(() => { setUnavailable([]); setAmOnly([]); setPmOnly([]); });
  }, [spaceId, dateRange.to]);

  const disabledDates = useMemo(() => {
    const set = new Set(unavailable);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return (d) => {
      if (d < today) return true;
      const iso = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
      return set.has(iso);
    };
  }, [unavailable]);

  const dayModifiers = useMemo(() => {
    const toDate = (iso) => {
      const [y, m, day] = iso.split("-").map(Number);
      return new Date(y, m - 1, day);
    };
    return {
      am_booked: amOnly.map(toDate),
      pm_booked: pmOnly.map(toDate),
    };
  }, [amOnly, pmOnly]);

  const modifiersClassNames = {
    am_booked: "relative after:content-['AM'] after:absolute after:top-0 after:right-0 after:text-[8px] after:bg-[#E0B66A] after:text-[#0D2C4D] after:px-1 after:rounded-bl after:font-bold",
    pm_booked: "relative after:content-['PM'] after:absolute after:top-0 after:right-0 after:text-[8px] after:bg-[#0D2C4D] after:text-white after:px-1 after:rounded-bl after:font-bold",
  };

  const computedPrice = useMemo(() => {
    if (!space) return 0;
    if (paymentMethod === "pass") return 0;
    if (bookingMode === "range") {
      if (!dateRange.from || !dateRange.to) return 0;
      const days = Math.round((dateRange.to - dateRange.from) / 86400000) + 1;
      return space.price_full_day * (space.type === "open_space" ? seats : 1) * days;
    }
    if (slotType === "full_day") return space.price_full_day * seats;
    if (slotType === "morning" || slotType === "afternoon") return space.price_half_day * seats;
    if (slotType === "hour" && hourStart != null) return space.price_hour;
    return 0;
  }, [space, slotType, seats, hourStart, bookingMode, dateRange, paymentMethod]);

  const submit = async () => {
    if (!user) { navigate("/connexion?next=/reserver"); return; }
    if (!space) { toast.error("Sélectionnez un espace"); return; }

    setSubmitting(true);
    try {
      if (bookingMode === "range") {
        if (!dateRange.from || !dateRange.to) { toast.error("Sélectionnez une période"); setSubmitting(false); return; }
        const toIso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
        const payload = {
          space_id: space.id,
          start_date: toIso(dateRange.from),
          end_date: toIso(dateRange.to),
          seats: space.type === "open_space" ? seats : 1,
          payment_method: paymentMethod,
        };
        const { data: r } = await api.post("/reservations/range", payload);

        if (paymentMethod === "card") {
          const origin = window.location.origin;
          const { data: c } = await api.post("/payments/checkout-batch", { batch_id: r.batch_id, origin_url: origin });
          window.location.href = c.url;
          return;
        }
        toast.success(`${r.days} jour(s) réservé(s) — ${r.total_price} € à régler sur place`);
        navigate("/mon-compte");
        return;
      }

      if (!isoDate) { toast.error("Sélectionnez une date"); return; }
      if (slotType === "hour" && hourStart == null) { toast.error("Sélectionnez un créneau horaire"); return; }
      const payload = {
        space_id: space.id, date: isoDate, slot_type: slotType,
        start_hour: slotType === "hour" ? hourStart : null,
        end_hour: slotType === "hour" ? hourStart + 1 : null,
        seats: space.type === "open_space" ? seats : 1,
        payment_method: paymentMethod,
      };
      const { data: r } = await api.post("/reservations", payload);

      if (paymentMethod === "card") {
        const origin = window.location.origin;
        const { data: c } = await api.post("/payments/checkout", { reservation_id: r.id, origin_url: origin });
        window.location.href = c.url;
        return;
      }
      toast.success("Réservation confirmée — paiement sur place");
      navigate("/mon-compte");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-x py-12" data-testid="reserver-page">
      <p className="overline text-[#2B8B8E]">Réservation</p>
      <h1 className="text-4xl sm:text-5xl font-light mt-2 mb-6">Réservez votre espace.</h1>

      {/* Reservation mode toggle */}
      <div className="grid grid-cols-2 gap-3 max-w-xl mb-10" data-testid="reservation-mode-toggle">
        <button
          onClick={() => setReservationMode("carte")}
          data-testid="mode-carte"
          className={`p-4 rounded-2xl border-2 text-sm font-semibold transition ${reservationMode === "carte" ? "border-[#2B8B8E] bg-[#2B8B8E]/8 text-[#0D2C4D]" : "border-border hover:border-[#2B8B8E]/40 text-muted-foreground"}`}
        >
          À la carte
          <span className="block text-[10px] font-normal mt-1">Réservation ponctuelle sans engagement</span>
        </button>
        <button
          onClick={() => setReservationMode("pass")}
          data-testid="mode-pass"
          className={`p-4 rounded-2xl border-2 text-sm font-semibold transition ${reservationMode === "pass" ? "border-[#2B8B8E] bg-[#2B8B8E]/8 text-[#0D2C4D]" : "border-border hover:border-[#2B8B8E]/40 text-muted-foreground"}`}
        >
          Nos Pass
          <span className="block text-[10px] font-normal mt-1">Nomade (jusqu'à 2 mois) · Résident (1 à 12 mois)</span>
        </button>
      </div>

      {reservationMode === "pass" && <PassesFlow />}
      {reservationMode === "carte" && (<>

      <div className="rounded-2xl bg-[#2B8B8E]/5 border border-[#2B8B8E]/20 p-5 mb-10" data-testid="how-to-reserve">
        <p className="overline text-[#2B8B8E] mb-3">Comment réserver ?</p>
        <ol className="flex flex-col md:flex-row md:items-center gap-3 md:gap-1 text-sm">
          {["Choisissez votre espace", "Sélectionnez votre date", "Choisissez votre créneau", "Vérifiez votre réservation", "Confirmez"].map((s, i) => (
            <li key={s} className="flex items-center gap-2 md:flex-1">
              <span className="w-6 h-6 rounded-full bg-[#2B8B8E] text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
              <span className="text-foreground/80">{s}</span>
              {i < 4 && <span className="hidden md:inline text-[#2B8B8E]/40">→</span>}
            </li>
          ))}
        </ol>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Choose space */}
        <div className="lg:col-span-7 space-y-6">
          <div>
            <Label className="overline mb-3 block">1. Choisissez un espace</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {spaces.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSpaceId(s.id)}
                  data-testid={`select-space-${s.type}`}
                  className={`text-left p-4 rounded-xl border-2 transition ${spaceId === s.id ? "border-[#2B8B8E] bg-[#2B8B8E]/5" : "border-border hover:border-[#2B8B8E]/40"}`}
                >
                  <p className="overline text-[#0D2C4D] text-[10px]">{spaceTypeLabel(s.type)}</p>
                  <p className="font-semibold mt-1 text-sm">{s.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">Capacité : {s.capacity}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="overline mb-3 block">2. Type de réservation</Label>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button type="button" onClick={() => setBookingMode("single")} data-testid="mode-single"
                className={`p-3 rounded-xl border-2 text-sm font-medium transition ${bookingMode === "single" ? "border-[#2B8B8E] bg-[#2B8B8E]/5" : "border-border"}`}>
                Un jour
              </button>
              <button type="button" onClick={() => setBookingMode("range")} data-testid="mode-range"
                className={`p-3 rounded-xl border-2 text-sm font-medium transition ${bookingMode === "range" ? "border-[#2B8B8E] bg-[#2B8B8E]/5" : "border-border"}`}>
                Période (plusieurs jours)
              </button>
            </div>
            {bookingMode === "single" ? (
              <div className="border border-border rounded-2xl p-4 bg-card inline-block">
                <Calendar mode="single" locale={fr} weekStartsOn={1} selected={date} onSelect={(d) => d && setDate(d)} disabled={disabledDates} modifiers={dayModifiers} modifiersClassNames={modifiersClassNames} data-testid="booking-calendar" />
                <p className="text-[10px] text-muted-foreground mt-3 max-w-[280px] leading-relaxed">
                  <span className="inline-block w-2 h-2 rounded-full bg-muted-foreground/40 mr-1"></span>
                  Date grisée = journée complète · <span className="inline-block bg-[#E0B66A] text-[#0D2C4D] px-1 rounded font-bold text-[9px] mx-0.5">AM</span> matinée réservée · <span className="inline-block bg-[#0D2C4D] text-white px-1 rounded font-bold text-[9px] mx-0.5">PM</span> après-midi réservé
                </p>
              </div>
            ) : (
              <div className="border border-border rounded-2xl p-4 bg-card inline-block">
                <Calendar mode="range" locale={fr} weekStartsOn={1} selected={dateRange} onSelect={(r) => r && setDateRange(r)} numberOfMonths={2} disabled={disabledDates} modifiers={dayModifiers} modifiersClassNames={modifiersClassNames} data-testid="booking-calendar-range" />
                {dateRange.from && dateRange.to && (
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Du {dateRange.from.toLocaleDateString("fr-FR")} au {dateRange.to.toLocaleDateString("fr-FR")}
                    {" — "}{Math.round((dateRange.to - dateRange.from) / 86400000) + 1} jour(s)
                  </p>
                )}
                <p className="text-[10px] text-muted-foreground mt-2 text-center">Date grisée = complète · AM/PM = demi-journée réservée</p>
                <p className="text-xs text-[#2B8B8E] mt-2 text-center">💳 Paiement par carte bancaire uniquement</p>
              </div>
            )}
          </div>

          <div>
            <Label className={`overline mb-3 block ${bookingMode === "range" ? "hidden" : ""}`}>3. Type de créneau</Label>
            <div className={bookingMode === "range" ? "hidden" : ""}>
            {space?.type === "open_space" ? (
              <RadioGroup value={slotType} onValueChange={setSlotType} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {availability?.slots?.map((s) => (
                  <label key={s.slot_type} className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer ${slotType === s.slot_type ? "border-[#2B8B8E]" : "border-border"} ${!s.available && "opacity-50"}`}>
                    <RadioGroupItem value={s.slot_type} disabled={!s.available} data-testid={`slot-${s.slot_type}`} />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{s.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {s.available ? `${s.remaining} place(s) restante(s)` : "Complet"}
                      </p>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            ) : (
              <div className="space-y-4">
                <RadioGroup value={slotType} onValueChange={(v) => { setSlotType(v); if (v !== "hour") setHourStart(null); }} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {availability?.slots?.map((s) => (
                    <label key={s.slot_type} className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer ${slotType === s.slot_type ? "border-[#2B8B8E]" : "border-border"} ${!s.available && "opacity-50"}`}>
                      <RadioGroupItem value={s.slot_type} disabled={!s.available} data-testid={`slot-${s.slot_type}`} />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{s.label}</p>
                        <p className="text-xs text-muted-foreground">{s.available ? "Disponible" : "Réservé"}</p>
                      </div>
                    </label>
                  ))}
                </RadioGroup>
                {slotType === "hour" && availability?.hour_slots && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Choisissez une heure :</p>
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                      {availability.hour_slots.map((h) => (
                        <button
                          key={h.start_hour}
                          disabled={!h.available}
                          onClick={() => setHourStart(h.start_hour)}
                          data-testid={`hour-${h.start_hour}`}
                          className={`p-2 text-xs rounded-lg border ${hourStart === h.start_hour ? "bg-[#2B8B8E] text-white border-[#2B8B8E]" : h.available ? "border-border hover:border-[#2B8B8E]" : "opacity-30 cursor-not-allowed"}`}
                        >
                          {h.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <button onClick={() => setSlotType("hour")} className={`text-xs underline text-muted-foreground ${space?.price_hour ? "" : "hidden"}`}>Réserver à l'heure</button>
              </div>
            )}
            </div>
          </div>

          {space?.type === "open_space" && (
            <div>
              <Label className="overline mb-2 block">4. Nombre de places</Label>
              <Input type="number" min={1} max={space.capacity} value={seats} onChange={(e) => setSeats(parseInt(e.target.value) || 1)} className="max-w-[140px]" data-testid="seats-input" />
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="lg:col-span-5">
          <div className="sticky top-24 border border-border rounded-3xl p-7 bg-card" data-testid="reservation-summary">
            <p className="overline text-[#2B8B8E] mb-3">Récapitulatif</p>
            {space ? (
              <>
                <h3 className="text-2xl font-semibold mb-1">{space.name}</h3>
                <p className="text-xs text-muted-foreground mb-5">{spaceTypeLabel(space.type)}</p>
                <ul className="space-y-2 text-sm mb-6">
                  <li className="flex justify-between"><span className="text-muted-foreground">{bookingMode === "range" ? "Période" : "Date"}</span><span className="font-medium">{
                    bookingMode === "range"
                      ? (dateRange.from && dateRange.to
                          ? `${dateRange.from.toLocaleDateString("fr-FR")} → ${dateRange.to.toLocaleDateString("fr-FR")}`
                          : "—")
                      : isoDate
                  }</span></li>
                  {bookingMode === "range" ? (
                    <li className="flex justify-between"><span className="text-muted-foreground">Jours</span><span className="font-medium">{dateRange.from && dateRange.to ? Math.round((dateRange.to - dateRange.from) / 86400000) + 1 : 0}</span></li>
                  ) : (
                    <li className="flex justify-between"><span className="text-muted-foreground">Créneau</span><span className="font-medium">
                      {slotType === "hour" && hourStart != null ? `${hourStart}h - ${hourStart + 1}h` : slotLabel(slotType)}
                    </span></li>
                  )}
                  {space.type === "open_space" && <li className="flex justify-between"><span className="text-muted-foreground">Places</span><span className="font-medium">{seats}</span></li>}
                  <li className="flex justify-between text-base pt-3 border-t border-border"><span className="font-medium">Total</span><span className="font-bold text-[#2B8B8E]">{eur(computedPrice)}</span></li>
                </ul>
                <Label className="overline mb-2 block">Paiement</Label>
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-2 mb-5">
                  {passUsable && (
                    <label className="flex items-center gap-3 p-3 rounded-xl border-2 border-[#2B8B8E] bg-[#2B8B8E]/5 cursor-pointer">
                      <RadioGroupItem value="pass" data-testid="payment-pass" />
                      <div className="flex-1 text-sm">
                        <p className="font-semibold text-[#2B8B8E]">Utiliser mon {activePass?.label || "Pass"}</p>
                        <p className="text-xs text-muted-foreground">
                          {activePass?.unlimited ? "Accès illimité" : `${activePass?.days_remaining} journée(s) restante(s)`} · Valide jusqu'au {activePass?.valid_until}
                        </p>
                      </div>
                    </label>
                  )}                  <label className="flex items-center gap-3 p-3 rounded-xl border border-border cursor-pointer">
                    <RadioGroupItem value="card" data-testid="payment-card" />
                    <span className="text-sm">Carte bancaire (Stripe)</span>
                  </label>
                </RadioGroup>
                <Button onClick={submit} disabled={submitting || !computedPrice} className="w-full bg-[#2B8B8E] hover:bg-[#1F6F73] text-white rounded-full" data-testid="confirm-reservation-button">
                  {submitting ? "Validation…" : "Confirmer la réservation"}
                </Button>
                {!user && <p className="text-xs text-center text-muted-foreground mt-3">Vous serez invité à vous connecter</p>}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Sélectionnez un espace pour continuer.</p>
            )}
          </div>
        </div>
      </div>
      </>)}
    </div>
  );
}
